import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type AjaxService from 'garaje/services/ajax';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type ResourcesCsvImporterService from 'garaje/services/resources-csv-importer';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

import type { ResourcesResourceTypeImportRouteModel } from './route';

// Interface for resource CSV data
interface ResourceCsvData {
  envoyId: string;
  name: string;
  floorName: string;
  amenities: string;
  assignedTo: string;
}

export default class ResourcesResourceTypeImportController extends Controller {
  @service declare router: RouterService;
  @service declare ajax: AjaxService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare resourcesCsvImporter: ResourcesCsvImporterService;
  @tracked file: File | null = null;
  @tracked records: ResourceCsvData[] = [];

  @dropTask
  *uploadFileTask(): Generator<unknown, void, unknown> {
    const { id } = this.state.currentLocation;
    const { resourceTypeId } = this.model as ResourcesResourceTypeImportRouteModel;
    const uploadUrl = urlBuilder.rms.uploadResourcesCsv(id, resourceTypeId);

    const formData = new FormData();
    formData.append('file', this.file!);

    try {
      yield this.ajax.request(uploadUrl, {
        // @ts-expect-error - accept is not a valid option
        accept: 'application/vnd.api+json',
        contentType: false,
        data: formData,
        processData: false,
        type: 'POST',
      });
      yield this.store.unloadAll('generic-resource');
      this.flashMessages.showAndHideFlash('success', 'File uploaded!');
      this.router.transitionTo('resources.resource-type.index', resourceTypeId);
    } catch (err: unknown) {
      this.flashMessages.showAndHideFlash('error', 'There was an error with your csv', parseErrorForDisplay(err));
    }
  }

  @action
  clearAll(): void {
    this.file = null;
  }
}
